<script lang="ts" setup>
    import { ref, computed } from "vue";
    import type { PropType } from "vue";
    import VueHorizontal from "vue-horizontal";
    import RothcoCta from "@/components/RothcoCta.vue";
    import { useAnchorListener } from "@/helpers/composables/useAnchorListener";
    import type { Cta } from "@/helpers/builder-init/index";
    import { getCtaLink } from "@/helpers/interfaces/Cta";
    import useImageOptimization from "@/helpers/composables/useImageOptimization";
    import type {
        Text,
        Badge,
        Media
    } from "@/helpers/interfaces/BuilderFields.ts";
    import {
        useRichTextFormatting,
        richTextHasText
    } from "@/helpers/composables/builderFieldUtility.ts";

    interface Column {
        heading: Text;
        description: Text;
        descriptionPosition: string;
        cta: Cta;
        badge?: Badge;
        media?: Media;
    }

    const props = defineProps({
        bg_color: {
            type: String,
            default: "rothcoWhite"
        },
        column_one: {
            type: Object as PropType<Column>,
            default() {
                return { heading: {}, descriptionPosition: {}, description: {}, cta: {}, badge: {} };
            }
        },
        column_two: {
            type: Object as PropType<Column>,
            default() {
                return { heading: {}, description: {}, cta: {}, media: {} };
            }
        },
        column_three: {
            type: Object as PropType<Column>,
            default() {
                return { heading: {}, description: {}, cta: {}, media: {} };
            }
        },
        column_four: {
            type: Object as PropType<Column>,
            default() {
                return { heading: {}, description: {}, cta: {}, media: {} };
            }
        }
    });

    const columnOneTextContainer = ref<HTMLElement | null>(null);

    useAnchorListener(columnOneTextContainer);

    let bgColorClass = computed(() => {
        return `bg-${props.bg_color}`;
    });

    let getTextColorClass = (color) => `text-${color}`;

    const getCtaClasses = (cta) => {
        let classes = [cta.style ? cta.style : "primary"];
        if (cta.size != "default") classes.push(cta.size);
        return classes;
    };

    let scrollableColumns = computed(() => {
        return [props.column_two, props.column_three, props.column_four];
    });

    const cssVars = computed(() => {
        return {
            "--badge-width-desktop": props.column_one.badge?.width_desktop,
            "--badge-width-mobile": props.column_one.badge?.width_mobile
        };
    });

    let { optimize } = useImageOptimization(); 
</script>
<template>
    <div :class="['product-systems', bgColorClass]" :style="cssVars">
        <div class="product-systems-header">
            <span
                v-if="props.column_one.heading.text"
                :class="[
                    'headline-l',
                    getTextColorClass(props.column_one.heading.color)
                ]"
                role="heading"
                aria-level="2"
                >{{ props.column_one.heading.text }}</span
            >
            <p
                ref="columnOneTextContainer"
                v-if="richTextHasText(props.column_one.description.text)  && (props.column_one.descriptionPosition == null || props.column_one.descriptionPosition == 'above cta')"
                :class="[getTextColorClass(props.column_one.description.color)]"
                v-html="
                    useRichTextFormatting(props.column_one.description.text)
                "
            ></p>
            <RothcoCta
                v-if="props.column_one.cta.label"
                :ctaType="props.column_one.cta.type"
                :class="[...getCtaClasses(props.column_one.cta)]"
                v-bind="getCtaLink(props.column_one.cta)"
                :aria-label="props.column_one.cta.aria_label"
            >
                {{ props.column_one.cta.label }}
            </RothcoCta>
            <p
                ref="columnOneTextContainer"
                v-if="richTextHasText(props.column_one.description.text)  && (props.column_one.descriptionPosition == 'below cta')"
                :class="[getTextColorClass(props.column_one.description.color)]"
                v-html="
                    useRichTextFormatting(props.column_one.description.text)
                "
            ></p>
            <img
                v-if="props.column_one.badge && props.column_one.badge.image"
                :src="optimize(props.column_one.badge.image)"
                :alt="props.column_one.badge.alt_text"
                class="layering_systems"
            />
        </div>
        <vue-horizontal
            responsive
            :button="false"
            class="product-systems-grid"
        >
            <div
                v-for="(column, index) in scrollableColumns"
                :key="index"
                class="product-systems-item"
            >
                <span
                    :class="[
                        'headline-xs',
                        getTextColorClass(column.heading.color)
                    ]"
                    role="heading"
                    aria-level="3"
                    >{{ column.heading.text }}</span
                >
                <p
                    v-if="(column.descriptionPosition == null || column.descriptionPosition == 'above cta')"
                    :class="[getTextColorClass(column.description.color)]"
                    v-html="useRichTextFormatting(column.description.text)"
                ></p>
                <RothcoCta
                    :ctaType="column.cta.type"
                    v-if="column.cta.label"
                    :class="[...getCtaClasses(column.cta)]"
                    v-bind="getCtaLink(column.cta)"
                    :aria-label="column.cta.aria_label"
                >
                    {{ column.cta.label }}
                </RothcoCta>
                <p
                    v-if="(column.descriptionPosition == 'below cta')"
                    :class="[getTextColorClass(column.description.color)]"
                    v-html="useRichTextFormatting(column.description.text)"
                ></p>
                <img
                    v-if="column.media?.image"
                    loading="lazy"
                    :src="optimize(column.media?.image)"
                    :alt="column.media?.alt_text"
                    :aria-label="column.media?.aria_label"
                    class="item-img"
                />
            </div>
        </vue-horizontal>
    </div>
</template>